import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

export default function WaiterLoginModal({ isOpen, onClose, onSuccess }) {
  const [waiterId, setWaiterId] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(false);
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  // Fetch waiters from API when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchWaiters();
    }
  }, [isOpen]);

  const fetchWaiters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/api/users/waiters`);
      setWaiters(response.data);
    } catch (error) {
      toast.error('Failed to load waiters');
      console.error('Error fetching waiters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!waiterId || !pin) {
      setError('Please select a waiter and enter PIN');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${apiUrl}/api/users/verifyPin`, {
        waiterId,
        pin
      });

      // On successful verification, call onSuccess with waiter data
      const authenticatedWaiter = response.data.waiter;
      onSuccess(authenticatedWaiter);
      
      // Reset form
      setWaiterId('');
      setPin('');
      setError('');
    } catch (error) {
      setError(error.response?.data?.error || 'Authentication failed');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-80 shadow-xl">
        <h2 className="text-lg font-semibold mb-4">Waiter Login</h2>

        {loading && waiters.length === 0 ? (
          <div className="text-center py-4">Loading waiters...</div>
        ) : (
          <>
            <select
              value={waiterId}
              onChange={(e) => setWaiterId(e.target.value)}
              className="w-full p-2 border rounded mb-3"
              disabled={loading}
            >
              <option value="">-- Select waiter --</option>
              {waiters.map((waiter) => (
                <option key={waiter._id} value={waiter._id}>
                  {waiter.username}
                </option>
              ))}
            </select>

            <input
              type="password"
              placeholder="Enter PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className="w-full p-2 border rounded mb-2"
              disabled={loading}
            />

            {error && <div className="text-red-500 text-sm mb-2">{error}</div>}

            <div className="flex justify-end gap-2">
              <button 
                onClick={onClose} 
                className="px-3 py-1 bg-gray-300 rounded"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmit} 
                className="px-3 py-1 bg-blue-600 text-white rounded"
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Login'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}